import React, { useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import { InputAdornment } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Importing assets
import lockicon from "../../assets/svg/lock.svg";
import gmailicon from "../../assets/svg/gmailicon.svg";
import message1 from "../../assets/img/message1.png";
import settings from "../../assets/img/settings.png";
import eye from "../../assets/img/eye.png";

// Import your API functions
import { sendOtp, loginWithOtp, login } from "../../api/Api";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

function Signin() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpVisible, setOtpVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [useOtp, setUseOtp] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const navigate = useNavigate();

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setOtpVisible(false);
    setOtp("");
    setLoading(false);
    setUseOtp(false);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSendOtp = async () => {
    setLoading(true);
    try {
      if (!otpVisible) {
        await sendOtp({ email });
        setOtpVisible(true);
        showSnackbar("OTP sent successfully", "success");
      } else {
        const responseData = await loginWithOtp({ email, otp, role: "admin" });
        localStorage.setItem("pd", JSON.stringify(responseData.data));
        showSnackbar("Sign In Success!", "success");
        setTimeout(() => {
          navigate("/Dashboard");
          handleClose();
        }, 3000);
      }
    } catch (error) {
      console.error("Error:", error);
      showSnackbar(
        error.response?.data?.message || "Failed to sign in. Try again.",
        "error"
      );
    }
    setLoading(false);
  };

  const handleEmailPasswordLogin = async () => {
    setLoading(true);
    try {
      const responseData = await login({ email, password, role: "admin" });
      localStorage.setItem("pd", JSON.stringify(responseData.data));
      showSnackbar("Login Successful", "success");
      navigate("/Dashboard");
      handleClose();
    } catch (error) {
      console.error("Login failed:", error);
      showSnackbar(
        error.response?.data?.message || "Login failed. Check credentials.",
        "error"
      );
    }
    setLoading(false);
  };

  return (
    <div>
      <Button
        variant="contained"
        onClick={handleOpen}
        sx={{
          padding: "10px",
          width: "8rem",
          borderRadius: "20px",
          textTransform: "capitalize",
          background: theme.palette.primary.main,
        }}
      >
        Admin Sign In
      </Button>

      <Modal open={open} onClose={handleClose}>
        <Box sx={style} component="form">
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center">
              <img src={lockicon} alt="lock icon" width="24" height="24" style={{ marginRight: 8 }} />
              <Typography variant="h5">Sign In</Typography>
            </Box>
            <Box display="flex" gap={2}>
              <IconButton onClick={() => setUseOtp(false)} color={!useOtp ? "primary" : "default"}>
                <Avatar
                  sx={{
                    bgcolor: "transparent",
                    width: 40,
                    height: 40,
                    border: !useOtp ? "2px solid blue" : "1px solid #ddd",
                  }}
                >
                  <img src={gmailicon} alt="gmail icon" style={{ width: "30px", height: "30px" }} />
                </Avatar>
              </IconButton>

              <IconButton onClick={() => setUseOtp(true)} color={useOtp ? "primary" : "default"}>
                <Avatar
                  sx={{
                    bgcolor: "transparent",
                    width: 40,
                    height: 40,
                    border: useOtp ? "2px solid blue" : "1px solid #ddd",
                  }}
                >
                  <img src={message1} alt="phone icon" style={{ width: "30px", height: "30px" }} />
                </Avatar>
              </IconButton>
            </Box>
          </Box>

          <TextField fullWidth label="Email*" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" sx={{ mb: 2 }} disabled={otpVisible} />

          {useOtp ? (
            otpVisible ? (
              <>
                <TextField fullWidth label="OTP" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Enter OTP" sx={{ mb: 3 }} />
                <Button variant="contained" onClick={handleSendOtp} sx={{ width: "100%", padding: "12px", backgroundColor: theme.palette.primary.light, borderRadius: "5px" }} disabled={loading}>
                  {loading ? "Processing..." : "Sign In"}
                </Button>
              </>
            ) : (
              <Button variant="contained" onClick={handleSendOtp} sx={{ width: "100%", padding: "11px", backgroundColor: theme.palette.primary.light, borderRadius: "5px" }} disabled={loading}>
                {loading ? "Processing..." : "Send OTP"}
              </Button>
            )
          ) : (
            <>
              <TextField
                fullWidth
                label="Password*"
                type={passwordVisible ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <img src={eye} onClick={() => setPasswordVisible(!passwordVisible)} alt="toggle visibility" style={{ cursor: "pointer" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button variant="contained" onClick={handleEmailPasswordLogin} sx={{ width: "100%", padding: "12px", backgroundColor: theme.palette.primary.light, borderRadius: "5px" }} disabled={loading}>
                {loading ? "Processing..." : "Sign In"}
              </Button>
            </>
          )}
        </Box>
      </Modal>

      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default Signin;
